/**
 * Reusable JSON code block modal component for displaying formatted JSON data.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Box, Modal, ModalBody, Stack, CodeBlock, SkeletonShape } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  CODE_BLOCK_THEME,
  MODAL_CONTENT_NEGATIVE_MARGIN,
  MODAL_CONTENT_EXTRA_WIDTH,
} from '../../constants/code-block-theme';

export interface JsonCodeBlockModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  code: string;
  skeletonRows?: number;
}

export function JsonCodeBlockModal({ isOpen, onClose, title, code, skeletonRows = 10 }: JsonCodeBlockModalProps) {
  const [isReady, setIsReady] = useState(false);
  const codeBlockId = useMemo(() => `json-modal-${Math.random().toString(36).slice(2, 11)}`, []);

  // Use effect to detect when modal opens and prepare CodeBlock
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame for next paint
      const frameId = requestAnimationFrame(() => setIsReady(true));
      return () => cancelAnimationFrame(frameId);
    } else {
      setIsReady(false);
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <ModalBody>
        {/* Screen reader description */}
        <div id={`${codeBlockId}-description`} style={{ position: 'absolute', left: '-9999px' }}>
          JSON data viewer with syntax highlighting and copy functionality
        </div>
        <Box
          p="0"
          aria-describedby={`${codeBlockId}-description`}
          style={{
            margin: `${MODAL_CONTENT_NEGATIVE_MARGIN} ${MODAL_CONTENT_NEGATIVE_MARGIN} ${MODAL_CONTENT_NEGATIVE_MARGIN} ${MODAL_CONTENT_NEGATIVE_MARGIN}`,
            backgroundColor: CODE_BLOCK_THEME.background,
            border: 'none',
            boxShadow: 'none',
            overflow: 'hidden',
            width: MODAL_CONTENT_EXTRA_WIDTH,
          }}
        >
          <style>{`
            [data-codeblock-id="${codeBlockId}"] code,
            [data-codeblock-id="${codeBlockId}"] pre code,
            [data-codeblock-id="${codeBlockId}"] code * {
              background-color: transparent !important;
            }
            [data-codeblock-id="${codeBlockId}"] pre {
              background-color: ${CODE_BLOCK_THEME.background} !important;
            }
            [data-codeblock-id="${codeBlockId}"] {
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background-color: ${CODE_BLOCK_THEME.background} !important;
              outline: none !important;
            }
            [data-codeblock-id="${codeBlockId}"] * {
              border: none !important;
              outline: none !important;
            }
            [data-codeblock-id="${codeBlockId}"] pre,
            [data-codeblock-id="${codeBlockId}"] code {
              color: ${CODE_BLOCK_THEME.text} !important;
            }
          `}</style>
          {!isReady ? (
            <Box
              pt="300"
              pb="100"
              pl="150"
              pr="150"
              style={{
                minHeight: '200px',
                maxHeight: '600px',
                backgroundColor: CODE_BLOCK_THEME.background,
              }}
            >
              <Stack gap="075">
                {Array.from({ length: skeletonRows }).map((_, index) => (
                  <SkeletonShape
                    key={index}
                    style={{
                      height: '16px',
                      width: index % 3 === 0 ? '100%' : index % 3 === 1 ? '85%' : '70%',
                      borderRadius: themeVars.radius['2'],
                      backgroundColor: CODE_BLOCK_THEME.skeletonHighlight,
                    }}
                  />
                ))}
              </Stack>
            </Box>
          ) : (
            <CodeBlock
              data-codeblock-id={codeBlockId}
              code={code}
              language="json"
              withLineFoldingControls={true}
              withCopyButton={true}
              withLineNumbers={true}
              mode="dark"
              darkTheme="night-owl"
              isRounded={false}
              pt="300"
              pb="100"
              pl="150"
              style={{
                minHeight: '200px',
                maxHeight: '600px',
                backgroundColor: CODE_BLOCK_THEME.background,
                border: 'none',
                margin: 0,
                boxShadow: 'none',
              }}
            />
          )}
        </Box>
      </ModalBody>
    </Modal>
  );
}
